import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import {FormArray, FormGroup} from "@angular/forms";
import {
  BokkzJrLogic,
  ClientEntity, Jaarrapportage,
  JaarrapportageEntity, Jaarrekening, TrialBalanceItem
} from "@snelstart-ventures/fiscaal/domain-logic";
import {BokkzAlgDataService, BokkzJrDataService} from "@snelstart-ventures/fiscaal/domain-logic";
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { combineLatest, distinctUntilChanged, Observable, ReplaySubject, Subject, takeUntil } from "rxjs";
import { SpecTrialbalanceComponent } from '../spec-trialbalance/spec-trialbalance.component';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-bedrijfsopbrengsten',
  templateUrl: './spec-bedrijfsopbrengsten.component.html',
  imports: [
    SpecTrialbalanceComponent,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    CommonModule
  ],
  styleUrls: ['./spec-bedrijfsopbrengsten.component.scss']
})
export class SpecBedrijfsopbrengstenComponent implements OnInit,OnChanges,OnDestroy {

  @Input() public id!: string;
  @Input() selectedTabObservable!: Observable<number>;
  private selectedTabSubject = new ReplaySubject<number>(1);
  showCustomControls = true;
  private destroy$ = new Subject<void>();
  public selectedTab = 0;
  public index = 0;
  public dialogVisible = false;
  public client = {} as ClientEntity;
  public jrEntity = {} as JaarrapportageEntity;
  public isRechtspersoon = false;
  public heeftAandelen = false;

  public operatingIncomeFg = {} as FormGroup;
  public netRevenueFg = {} as FormGroup;
  public changeValueAgriculturalStocksFg = {} as FormGroup;
  public changesInventoriesWorkInProgressFg = {} as FormGroup;
  public capitalisedProductionCostsFg = {} as FormGroup;
  public privateUseOwnBusinessFg = {} as FormGroup;
  public exploitationSubsidiesFg = {} as FormGroup;
  public operatingIncomeOtherFg = {} as FormGroup;

  public revenuesFromSaleOfGoodsFg = {} as FormGroup;
  public revenuesFromProvidingServicesFg = {} as FormGroup;
  public revenuesFromInterestFg = {} as FormGroup;
  public revenuesFromRoyaltiesFg = {} as FormGroup;
  public revenuesFromDividendsFg = {} as FormGroup;
  public revenuesFromLicensesFg = {} as FormGroup;
  public revenuesOtherFg = {} as FormGroup;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: "Ja"},
    {value: false, label: "Nee"},
  ];

  public showDialog(input: number) {
    this.index = input
    this.dialogVisible = true;
  }

  constructor(
    public algDataService: BokkzAlgDataService,
    private cdRef: ChangeDetectorRef,
    private jrLogicService: BokkzJrLogic,
    public jrDataService: BokkzJrDataService) {
    this.createFormGroups();
  }

  ngOnInit(): void {
    if (this.selectedTabObservable) {
      this.selectedTabObservable
        .pipe(takeUntil(this.destroy$))
        .subscribe(tab => {
          this.selectedTabSubject.next(tab);
        });
    }

    combineLatest([
      this.selectedTabSubject.pipe(distinctUntilChanged()),
      this.jrDataService.jaarrapportageEntity
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([tab, jrEntity]) => {
        this.selectedTab = tab;
        this.jrEntity = jrEntity;
        this.patchData(jrEntity);
      });
    this.jrDataService.isRechtspersoon.subscribe((value) => {
      this.isRechtspersoon = value;
    })
    this.jrDataService.heeftAandelen.subscribe((value) => {
      this.heeftAandelen = value;
    })
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedTabObservable']) {
      this.selectedTabObservable = changes['selectedTabObservable'].currentValue;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public createFormGroups() {
    this.operatingIncomeFg = this.jrDataService.createFormGroupMonetaryNoDecimalsPeriodValues()
    this.netRevenueFg = this.jrDataService.createFormGroupMonetaryNoDecimalsPeriodValues()
    this.changeValueAgriculturalStocksFg = this.jrDataService.createFormGroupBalanceItem();
    this.changesInventoriesWorkInProgressFg = this.jrDataService.createFormGroupBalanceItem();
    this.capitalisedProductionCostsFg = this.jrDataService.createFormGroupBalanceItem();
    this.privateUseOwnBusinessFg = this.jrDataService.createFormGroupBalanceItem();
    this.exploitationSubsidiesFg = this.jrDataService.createFormGroupBalanceItem();
    this.operatingIncomeOtherFg = this.jrDataService.createFormGroupBalanceItem();

    this.revenuesFromSaleOfGoodsFg = this.jrDataService.createFormGroupBalanceItem();
    this.revenuesFromProvidingServicesFg = this.jrDataService.createFormGroupBalanceItem();
    this.revenuesFromInterestFg = this.jrDataService.createFormGroupBalanceItem();
    this.revenuesFromRoyaltiesFg = this.jrDataService.createFormGroupBalanceItem();
    this.revenuesFromDividendsFg = this.jrDataService.createFormGroupBalanceItem();
    this.revenuesFromLicensesFg = this.jrDataService.createFormGroupBalanceItem();
    this.revenuesOtherFg = this.jrDataService.createFormGroupBalanceItem();
  }

  private patchData(value: JaarrapportageEntity) {

    const item = value.rapportage?.jaarrekeningen?.at(0)?.winstEnVerliesrekening;

    if (item) {
      this.operatingIncomeFg.reset();

      if (item?.bedrijfsopbrengsten?.operatingIncome) {
        this.operatingIncomeFg.patchValue(item.bedrijfsopbrengsten?.operatingIncome, {emitEvent: false})
      }

      //wijkt af, want is telling met lagere uitsplisting
      const nettoOmzet = item?.nettoOmzet;

      if (nettoOmzet) {
        this.netRevenueFg.reset();

        if (nettoOmzet.netRevenue) {
          this.netRevenueFg.patchValue(nettoOmzet.netRevenue, {emitEvent: false})
        }

        const uitsplitsing = nettoOmzet.nettoOmzetUitsplitsing;

        if (uitsplitsing) {
          if (uitsplitsing.revenuesFromSaleOfGoods) {
            const subItem = uitsplitsing.revenuesFromSaleOfGoods;
            this.revenuesFromSaleOfGoodsFg.patchValue(subItem)
            this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.revenuesFromSaleOfGoodsFg.get('trialBalanceItems') as FormArray)
          }
          if (uitsplitsing.revenuesFromProvidingServices) {
            const subItem = uitsplitsing.revenuesFromProvidingServices;
            this.revenuesFromProvidingServicesFg.patchValue(subItem)
            this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.revenuesFromProvidingServicesFg.get('trialBalanceItems') as FormArray)
          }
          if (uitsplitsing.revenuesFromInterest) {
            const subItem = uitsplitsing.revenuesFromInterest;
            this.revenuesFromInterestFg.patchValue(subItem)
            this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.revenuesFromInterestFg.get('trialBalanceItems') as FormArray)
          }
          if (uitsplitsing.revenuesFromRoyalties) {
            const subItem = uitsplitsing.revenuesFromRoyalties;
            this.revenuesFromRoyaltiesFg.patchValue(subItem)
            this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.revenuesFromRoyaltiesFg.get('trialBalanceItems') as FormArray)
          }
          if (uitsplitsing.revenuesFromDividends) {
            const subItem = uitsplitsing.revenuesFromDividends;
            this.revenuesFromDividendsFg.patchValue(subItem)
            this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.revenuesFromDividendsFg.get('trialBalanceItems') as FormArray)
          }
          if (uitsplitsing.revenuesFromLicenses) {
            const subItem = uitsplitsing.revenuesFromLicenses;
            this.revenuesFromLicensesFg.patchValue(subItem)
            this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.revenuesFromLicensesFg.get('trialBalanceItems') as FormArray)
          }
          if (uitsplitsing.revenuesOther) {
            const subItem = uitsplitsing.revenuesOther;
            this.revenuesOtherFg.patchValue(subItem)
            this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.revenuesOtherFg.get('trialBalanceItems') as FormArray)
          }
        }
      }

      if (item?.waardeveranderingenAgrarischeVoorraden?.changeValueAgriculturalStocks) {
        const subItem = item.waardeveranderingenAgrarischeVoorraden?.changeValueAgriculturalStocks;
        this.changeValueAgriculturalStocksFg.patchValue(subItem)
        this.updateTrialBalanceItems(subItem?.trialBalanceItems!, this.changeValueAgriculturalStocksFg.get('trialBalanceItems') as FormArray)
      }
      if (item?.waardeveranderingenVoorraad?.changesInventoriesWorkInProgress) {
        const subItem = item.waardeveranderingenVoorraad?.changesInventoriesWorkInProgress;
        this.changesInventoriesWorkInProgressFg.patchValue(subItem)
        this.updateTrialBalanceItems(subItem?.trialBalanceItems!, this.changesInventoriesWorkInProgressFg.get('trialBalanceItems') as FormArray)
      }
      if (item?.geactiveerdeProductieEigenBedrijf?.capitalisedProductionCosts) {
        const subItem = item.geactiveerdeProductieEigenBedrijf.capitalisedProductionCosts;
        this.capitalisedProductionCostsFg.patchValue(subItem)
        this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.capitalisedProductionCostsFg.get('trialBalanceItems') as FormArray)
      }
      if (item?.priveGebruikEigenBedrijf?.privateUseOwnBusiness) {
        const subItem = item.priveGebruikEigenBedrijf.privateUseOwnBusiness;
        this.privateUseOwnBusinessFg.patchValue(subItem)
        this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.privateUseOwnBusinessFg.get('trialBalanceItems') as FormArray)
      }
      if (item?.exploitatiesubsidies?.exploitationSubsidies) {
        const subItem = item.exploitatiesubsidies.exploitationSubsidies;
        this.exploitationSubsidiesFg.patchValue(subItem)
        this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.exploitationSubsidiesFg.get('trialBalanceItems') as FormArray)
      }
      if (item?.overigeBedrijfsopbrengsten?.operatingIncomeOther) {
        const subItem = item.overigeBedrijfsopbrengsten.operatingIncomeOther;
        this.operatingIncomeOtherFg.patchValue(subItem)
        this.updateTrialBalanceItems(subItem.trialBalanceItems!, this.operatingIncomeOtherFg.get('trialBalanceItems') as FormArray)
      }
    }
  }

  updateTrialBalanceItems(lijst?: TrialBalanceItem[], formArray?: FormArray) {
    if (lijst && formArray) {
      for (let i = 0; i < lijst.length; i++) {
        if (i < formArray.length) {
          const control = formArray.at(i) as FormGroup;
          control.patchValue(lijst[i], {emitEvent: false});
        } else {
          const formGroup = this.jrDataService.createFormGroupTrialBalanceItem(lijst[i])
          formArray.push(formGroup, {emitEvent: false});
        }
      }
    }
  }

  public saveForm(): void {
    this.showCustomControls = false;
    this.cdRef.detectChanges();
    this.operatingIncomeFg.updateValueAndValidity({ emitEvent: false });
    this.netRevenueFg.updateValueAndValidity({ emitEvent: false });
    this.changeValueAgriculturalStocksFg.updateValueAndValidity({ emitEvent: false });
    this.changesInventoriesWorkInProgressFg.updateValueAndValidity({ emitEvent: false });
    this.capitalisedProductionCostsFg.updateValueAndValidity({ emitEvent: false });
    this.privateUseOwnBusinessFg.updateValueAndValidity({ emitEvent: false });
    this.exploitationSubsidiesFg.updateValueAndValidity({ emitEvent: false });
    this.operatingIncomeOtherFg.updateValueAndValidity({ emitEvent: false });

    this.revenuesFromSaleOfGoodsFg.updateValueAndValidity({ emitEvent: false });
    this.revenuesFromProvidingServicesFg.updateValueAndValidity({ emitEvent: false });
    this.revenuesFromInterestFg.updateValueAndValidity({ emitEvent: false });
    this.revenuesFromRoyaltiesFg.updateValueAndValidity({ emitEvent: false });
    this.revenuesFromDividendsFg.updateValueAndValidity({ emitEvent: false });
    this.revenuesFromLicensesFg.updateValueAndValidity({ emitEvent: false });
    this.revenuesOtherFg.updateValueAndValidity({ emitEvent: false });

    if (this.jrEntity && this.jrEntity.rapportage && this.jrEntity.rapportage.jaarrekeningen) {
      this.jrEntity.rapportage.jaarrekeningen[0] = {
        ...this.jrEntity.rapportage.jaarrekeningen[0],
        winstEnVerliesrekening: {
          ...this.jrEntity.rapportage?.jaarrekeningen[0]?.winstEnVerliesrekening,
          nettoOmzet: {
            ...this.jrEntity.rapportage?.jaarrekeningen[0]?.winstEnVerliesrekening?.nettoOmzet,
            nettoOmzetUitsplitsing: {
              ...this.jrEntity.rapportage?.jaarrekeningen[0]?.winstEnVerliesrekening?.nettoOmzet?.nettoOmzetUitsplitsing,
              revenuesFromSaleOfGoods: this.revenuesFromSaleOfGoodsFg.value,
              revenuesFromProvidingServices: this.revenuesFromProvidingServicesFg.value,
              revenuesFromInterest: this.revenuesFromInterestFg.value,
              revenuesFromRoyalties: this.revenuesFromRoyaltiesFg.value,
              revenuesFromDividends: this.revenuesFromDividendsFg.value,
              revenuesFromLicenses: this.revenuesFromLicensesFg.value,
              revenuesOther: this.revenuesOtherFg.value,
            },
          },
          waardeveranderingenAgrarischeVoorraden: {
            ...this.jrEntity.rapportage?.jaarrekeningen[0]?.winstEnVerliesrekening?.waardeveranderingenAgrarischeVoorraden,
            changeValueAgriculturalStocks: this.changeValueAgriculturalStocksFg.value
          },
          waardeveranderingenVoorraad: {
            ...this.jrEntity.rapportage?.jaarrekeningen[0]?.winstEnVerliesrekening?.waardeveranderingenVoorraad,
            changesInventoriesWorkInProgress: this.changesInventoriesWorkInProgressFg.value
          },
          geactiveerdeProductieEigenBedrijf: {
            ...this.jrEntity.rapportage?.jaarrekeningen[0]?.winstEnVerliesrekening?.geactiveerdeProductieEigenBedrijf,
            capitalisedProductionCosts: this.capitalisedProductionCostsFg.value
          },
          priveGebruikEigenBedrijf: {
            ...this.jrEntity.rapportage?.jaarrekeningen[0]?.winstEnVerliesrekening?.priveGebruikEigenBedrijf,
            privateUseOwnBusiness: this.privateUseOwnBusinessFg.value
          },
          exploitatiesubsidies: {
            ...this.jrEntity.rapportage?.jaarrekeningen[0]?.winstEnVerliesrekening?.exploitatiesubsidies,
            exploitationSubsidies: this.exploitationSubsidiesFg.value
          },
          overigeBedrijfsopbrengsten: {
            ...this.jrEntity.rapportage?.jaarrekeningen[0]?.winstEnVerliesrekening?.overigeBedrijfsopbrengsten,
            operatingIncomeOther: this.operatingIncomeOtherFg.value
          }
        }
      } as Jaarrekening
    }

    this.jrEntity = {
      ...this.jrEntity,
      rapportage: {
        ...this.jrEntity.rapportage,
        jaarrekeningen: [this.jrEntity.rapportage?.jaarrekeningen?.at(0)]
      }
    } as Jaarrapportage
    this.jrDataService.updateEntity(this.jrEntity);
    this.jrLogicService.getCalculation(this.jrEntity)
    setTimeout(() => {
      this.showCustomControls = true;
      this.cdRef.detectChanges();
    });
  }

}
